import React from "react";
import { avatarSrc } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { MapPin, ArrowRight, Package, Car, User, Radio, ShieldCheck } from "lucide-react";

export const ACTIVE_DELIVERY_STATUSES = [
  "Accepted", "Pickup Pending", "Picked Up", "In Transit", "Arrived", "Out for Delivery",
];

export function ActiveDeliveryCard({ delivery: d, role, onOpen }) {
  if (!d) return null;
  const isSender = role === "sender";
  const traveller = d.traveller;

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-primary/20 bg-white shadow-sm"
      data-testid={`active-delivery-card-${role}`}
    >
      <div className="absolute inset-x-0 top-0 h-1 bg-primary" />
      <div className="p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-primary">
            {isSender ? <Package className="w-4 h-4" /> : <Car className="w-4 h-4" />}
            <span>{isSender ? "Package in Transit" : "Active Delivery"}</span>
          </div>
          <StatusBadge status={d.status} />
        </div>

        <div className="mt-4 flex items-center gap-2 text-sm font-medium text-slate-800">
          <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
          <span className="truncate">{d.pickup}</span>
          <ArrowRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
          <span className="truncate">{d.destination}</span>
        </div>

        {isSender ? (
          <div className="mt-4 flex items-center gap-3 rounded-xl bg-accent/50 p-3">
            {avatarSrc(traveller) ? (
              <img src={avatarSrc(traveller)} alt={traveller?.name} className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-primary"><Car className="w-5 h-5" /></div>
            )}
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-800 flex items-center gap-1 truncate">
                {traveller?.name || "Traveller"}
                {traveller?.verification_status === "verified" && <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />}
              </p>
              <p className="text-xs text-slate-400 truncate">{d.description}</p>
            </div>
          </div>
        ) : (
          <div className="mt-4 flex items-center gap-3 rounded-xl bg-accent/50 p-3">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-primary"><User className="w-5 h-5" /></div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-800 truncate">{d.sender_name || "Sender"}</p>
              <p className="text-xs text-slate-400 truncate">{d.description} · {d.category}</p>
            </div>
          </div>
        )}

        <div className="mt-3 flex items-center gap-2 text-xs font-medium" data-testid={`active-delivery-live-${role}`}>
          {d.live_tracking ? (
            <span className="flex items-center gap-1.5 text-rose-600">
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500" /></span>
              Live tracking active
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-slate-400"><Radio className="w-3.5 h-3.5" /> Live tracking starts after pickup</span>
          )}
        </div>

        <Button onClick={() => onOpen(d.id)} className="mt-4 w-full rounded-xl" data-testid={`active-delivery-cta-${role}`}>
          {isSender ? "View Live Tracking" : "Continue Delivery"}
        </Button>
      </div>
    </div>
  );
}
